import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { Icon } from './Icon.jsx';

export function EpicSearch({ value, valueTitle, onChange }) {
  const initial = value ? `${value}${valueTitle ? ` — ${valueTitle}` : ''}` : '';
  const [q, setQ] = useState(initial);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = (query) => {
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.searchEpics(query);
        setResults(data.epics || []);
        setAvailable(data.available !== false);
      } catch {
        setAvailable(false);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const onFocus = () => {
    setOpen(true);
    runSearch(q);
  };
  const onInput = (e) => {
    setQ(e.target.value);
    runSearch(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange(null);
    else if (!available) {
      onChange({ id: e.target.value.trim(), title: '' });
    }
  };
  const select = (epic) => {
    setQ(`${epic.id} — ${epic.title}`);
    onChange(epic);
    setOpen(false);
  };
  const clear = () => {
    setQ('');
    onChange(null);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        className="tt-input"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 8px 4px 14px',
        }}
      >
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name="search" size={15} />
        </span>
        <input
          type="text"
          value={q}
          onChange={onInput}
          onFocus={onFocus}
          placeholder={
            !available
              ? 'PROJ-123 (Jira unavailable — type manually)'
              : 'Search Jira epics — type a key or title'
          }
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            padding: '7px 0',
            background: 'transparent',
            fontSize: 14,
            color: 'var(--sp-fg-1)',
          }}
        />
        {q && (
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            style={{ width: 28, height: 28 }}
            onClick={clear}
            title="Clear"
          >
            <Icon name="x" size={13} />
          </button>
        )}
      </div>
      {!available && (
        <div
          className="tt-help"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: '#C4320A',
          }}
        >
          <Icon name="alert" size={12} /> Jira unavailable — epic ID entered
          manually
        </div>
      )}
      {open && available && (
        <div
          className="tt-pop"
          style={{ top: 'calc(100% + 4px)', left: 0, right: 0 }}
        >
          {loading && (
            <div className="tt-pop-item" style={{ color: 'var(--sp-fg-3)' }}>
              Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="tt-pop-item" style={{ color: 'var(--sp-fg-3)' }}>
              No matching epics
            </div>
          )}
          {!loading &&
            results.map((e) => (
              <div
                key={e.id}
                className="tt-pop-item"
                onClick={() => select(e)}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontFamily: 'DM Sans',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {e.id}
                </span>
                <span
                  style={{
                    color: 'var(--sp-fg-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {e.title}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
